import axios from 'axios';
axios.defaults.withCredentials = true;
import { fetchToken } from '../fetchToken';
const API_URL = process.env.EMAILS_API_URL;
import { logError } from '../../taskpane/utils/request';
import { calculateObjectHash, sanitizeEventId } from '../../taskpane/utils/calendar';

// Response Object from endpoint will either be the same response from getRecentlyViewed when
// an event isn't saved, or the following when a record is saved:
// {
//   "CalendarEvent": {
//     "id": "a0X8N0000001oMCUAY",
//     "relatedToApiName": "litify_pm__Matter__c",
//     "salesforceRelatedToObject": {
//       "objectId": "a0X8N0000001oMCUAY",
//       "objectApiName": "litify_pm__Matter__c",
//       "errorMessage": null,
//       "emailAddress": null,
//       "displayFields": {
//         "Id": "a0X8N0000001oMCUAY",
//         "litify_pm__Case_Title__c": null,
//         "Name": "MAT-2302200002"
//       }
//     }
//     "organizer": {
//       "objectId": "0058N0000022IUQQA2",
//       "objectApiName": "User",
//       "errorMessage": null,
//       "emailAddress": "zcochran@litify.onmicrosoft.com",
//       "displayFields": { ... }
//     },
//     "currentUser": {
//       "objectId": "0058N0000022IUQQA2",
//       "objectApiName": "User",
//       "errorMessage": null,
//       "emailAddress": "zcochran@litify.onmicrosoft.com",
//       "displayFields": { ... }
//     },
//     "attendees": [
//       {
//         "objectId": "0038N00000C9w3jQAB",
//         "objectApiName": "Contact",
//         "errorMessage": null,
//         "emailAddress": "zcochran@litify.com",
//         "displayFields": { ... }
//       }
//   }
// }
export const getCalendarEvent = async (eventId, ownerEmail, objectApiName, skipRecords) => {
  const token = await fetchToken();
  const config = {
    method: 'GET',
    url: API_URL + `calendar/events/${encodeURIComponent(sanitizeEventId(eventId))}?${ownerEmail ? `ownerEmail=${encodeURIComponent(ownerEmail)}&` : ''}objectApiName=${objectApiName}${skipRecords ? '&skipRecords=true' : ''}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    return await axios(config);
  } catch (e) {
    logError(e);
    throw e;
  }
};

export const calendarActiveSearch = async (objectApiName, searchString, contact = false) => {
  const token = await fetchToken();
  const config = {
    method: 'GET',
    url: `${API_URL}calendar/search?objectApiName=${objectApiName}&query=${encodeURIComponent(searchString)}${contact ? '&contact=true' : ''}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    return await axios(config);
  } catch (e) {
    logError(e);
    return e;
  }
};

// Response object from endpoint:
// {
//   "ProfileId": "00e8N000000iLgvQAE",
//   "AllRecords": [],
//   "ListViewRecords": [],
//   "organizer": [
//     {
//       "objectId": "0058N0000022IUQQA2",
//       "objectApiName": "User",
//       "errorMessage": null,
//       "emailAddress": "zcochran@litify.onmicrosoft.com",
//       "displayFields": { ... }
//     }
//   ],
//   "currentUser": {
//     "objectId": "0058N0000022IUQQA2",
//     "objectApiName": "User",
//     "errorMessage": null,
//     "emailAddress": "zcochran@litify.onmicrosoft.com",
//     "displayFields": { ... }
//   },
//   "attendees": [
//     {
//       "objectId": "0038N00000C9w3jQAB",
//       "objectApiName": "Contact",
//       "errorMessage": null,
//       "emailAddress": "zcochran@litify.com",
//       "displayFields": { ... }
//     }
//   ]
// }
export const getRecentlyViewed = async (objectApiName, ownerEmail, start, subject) => {
  const token = await fetchToken();
  let url = `${API_URL}calendar/recent?${ownerEmail ? `ownerEmail=${encodeURIComponent(ownerEmail)}&` : ''}objectApiName=${objectApiName}`;
  if (start && subject) {
    const presaveHash = calculateObjectHash({ start, subject });
    url = `${url}&presaveHash=${presaveHash}`;
  }
  const config = {
    method: 'GET',
    url,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    return await axios(config);
  } catch (e) {
    logError(e);
    throw e;
  }
};

export const saveEvent = async (requestBody, graphEventId, ownerEmail) => {
  const token = await fetchToken();

  const config = {
    method: 'PUT',
    url: API_URL + `calendar/events/${sanitizeEventId(graphEventId)}${ownerEmail ? `?ownerEmail=${encodeURIComponent(ownerEmail)}` : ''}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    data: requestBody,
  };

  try {
    return await axios(config);
  } catch (e) {
    logError(e);
    throw e;
  }
};

// TODO check if this needs support for ownerEmail
export const unSyncEvent = async (graphEventId) => {
  const token = await fetchToken();

  const config = {
    method: 'DELETE',
    url: API_URL + `calendar/events/${sanitizeEventId(graphEventId)}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    return await axios(config);
  } catch (e) {
    logError(e);
    throw e;
  }
};

/**
 * Uses the /contacts endpoint to manually query SF for user details. Returns the same response format
 * as the get /events/id endpoint
 * @param {[String]} emailAddresses Email addresses to search
 * @return {Object} an object where all of the email addresses are keys, and their values are an array of the responses from SF
 */
export const getSalesforceContacts = async (emailAddresses) => {
  const token = await fetchToken();
  const config = {
    method: 'POST',
    url: API_URL + `calendar/contacts`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    data: {
      emails: emailAddresses,
    },
  };

  try {
    const attendeeMapping = {};
    const response = await axios(config);
    for (const sfUser of response?.data?.results || []) {
      const email = sfUser.emailAddress.toLowerCase();
      if (attendeeMapping[email]) {
        attendeeMapping[email].push(sfUser);
      } else {
        attendeeMapping[email] = [sfUser];
      }
    }
    return attendeeMapping;
  } catch (e) {
    logError(e);
    return e;
  }
};

export const resyncEvents = async (ownerEmail, startDate, endDate) => {
  let url = API_URL + `calendar/resync`;
  const queryParamsArr = [];
  if (ownerEmail) {
    queryParamsArr.push(`ownerEmail=${encodeURIComponent(ownerEmail)}`);
  }
  if (startDate) {
    queryParamsArr.push(`startDate=${encodeURIComponent(startDate)}`);
  }
  if (endDate) {
    queryParamsArr.push(`endDate=${encodeURIComponent(endDate)}`);
  }

  if (queryParamsArr.length > 0) {
    url += `?${queryParamsArr.join('&')}`;
  }
  const token = await fetchToken();
  const config = {
    method: 'POST',
    url,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
  try {
    return await axios(config);
  } catch (e) {
    logError(e);
    return e;
  }
};

export const resyncComplete = async (ownerEmail, jobId) => {
  let url = API_URL + `calendar/resync/complete`;
  const queryParamsArr = [];
  if (ownerEmail) {
    queryParamsArr.push(`ownerEmail=${encodeURIComponent(ownerEmail)}`);
  }
  if (jobId) {
    queryParamsArr.push(`jobId=${encodeURIComponent(jobId)}`);
  }

  if (queryParamsArr.length > 0) {
    url += `?${queryParamsArr.join('&')}`;
  }
  const token = await fetchToken();
  const config = {
    method: 'POST',
    url,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
  try {
    return await axios(config);
  } catch (e) {
    logError(e);
    return e;
  }
};
